import { API_ENDPOINTS, getApiUrl } from '../config/api.config';
// Cookie authentication - credentials sent automatically

/**
 * Place an order with the trading API
 * @param {Object} orderData - Order details
 * @returns {Promise<Object>} - API response
 */
export const placeOrder = async (orderData) => {
  try {
    // Ensure we have proper request structure
    const requestData = formatOrderRequest(orderData);
    
    // Use centralized auth header function for consistency
    // This ensures all API calls to port 8080 have proper authentication
    const response = await fetch(getApiUrl(API_ENDPOINTS.ORDERS), {
      method: 'POST',
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify(requestData)
    });
    
    const data = await response.json();

    if (!response.ok) {
      throw new Error(data?.message || 'Failed to place order');
    }
    
    return data;
  } catch (error) {
    console.error('Error placing order:', error);
    throw error;
  }
};

/**
 * Format raw order data into the API-expected request structure
 * @param {Object} orderData - Raw order data from components
 * @returns {Object} - Formatted request for the API
 */
export const formatOrderRequest = (orderData) => {
  // Extract common fields - support both camelCase and snake_case for instrument_token
  const {
    instrumentToken,
    instrument_token,
    quantity,
    action,
    order_type,
  } = orderData;

  // Use whichever format was provided (snake_case takes precedence if both exist)
  const token = instrument_token ?? instrumentToken;

  // Simplified request structure exactly matching API requirements
  // Example: {"instrument_token":211185157,"order_type":"create_position_sell","quantity":20}
  return {
    instrument_token: Number(token), // Convert to number for proper API type handling
    quantity: Math.abs(parseInt(quantity, 10)),
    // Support both pre-formatted order_type and action-based format
    order_type: order_type || (action === 'BUY' ? 'create_position_buy' : 'create_position_sell')
  };
};


/**
 * Place a move order (away or closer)
 * @param {Object} position - Position data
 * @param {String} direction - 'away' or 'closer'
 * @param {Number} newStrike - Target strike
 * @param {Number} quantity - Absolute quantity to move
 * @returns {Promise<Object>} - API response
 */
export const placeMoveOrder = async (position, direction, newStrike, quantity) => {
  try {
    if (!position.instrument_token) {
      throw new Error('Instrument token is missing for the position.');
    }

    // Find the target instrument token from the moves data based on the target strike
    let targetInstrumentToken = 0;

    // Convert newStrike to number to ensure proper comparison
    const targetStrike = Number(newStrike);


    // Check if position has moves data
    if (position.moves && position.moves[direction] && Array.isArray(position.moves[direction])) {

      // Find the move option that matches the target strike
      const moveOption = position.moves[direction].find(move => Number(move.strike) === targetStrike);

      if (moveOption && moveOption.instrument_token) {
        targetInstrumentToken = Number(moveOption.instrument_token);
      } else {
      }
    } else {
    }

    // Create payload according to API struct requirements with absolute quantity
    const orderData = {
      instrument_token: Number(position.instrument_token), // Convert to number for proper API type handling
      order_type: direction === 'away' ? 'modify_position_away' : 'modify_position_closer',
      quantity: Math.abs(parseInt(quantity, 10)), // Send absolute quantity instead of percentage
      target_instrument_token: targetInstrumentToken
    };
    
    // Log the payload for debugging
    
    // Use centralized auth header function for consistent authentication
    const response = await fetch(getApiUrl(API_ENDPOINTS.ORDERS), {
      method: 'POST',
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify(orderData)
    });
    
    const data = await response.json();

    if (!response.ok) {
      throw new Error(data?.message || 'Failed to place move order');
    }
    
    return data;
  } catch (error) {
    console.error('Error placing move order:', error);
    throw error;
  }
};



/**
 * Place an exit order for a specific quantity
 * @param {Object} position - Position data
 * @param {Number} quantity - Quantity to exit (absolute value)
 * @returns {Promise<Object>} - API response
 */
export const placeExitOrder = async (position, quantity) => {
  try {
    if (!position.instrument_token) {
      throw new Error('Instrument token is missing for the position.');
    }

    // For exit: send negative qty for SELL positions (triggers BUY), positive for BUY positions (triggers SELL)
    const signedQuantity = position.quantity < 0
      ? -Math.abs(parseInt(quantity, 10))   // SELL position → send negative → backend does BUY
      : Math.abs(parseInt(quantity, 10));    // BUY position → send positive → backend does SELL

    const orderData = {
      instrument_token: Number(position.instrument_token),
      order_type: 'exit_position',
      quantity: signedQuantity
    };

    const response = await fetch(getApiUrl(API_ENDPOINTS.ORDERS), {
      method: 'POST',
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify(orderData)
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data?.message || 'Failed to exit position');
    }

    return data;
  } catch (error) {
    console.error('Error exiting position:', error);
    throw error;
  }
};

/**
 * Place an exit order for ALL open positions at once
 * @returns {Promise<Object>} - API response
 */
export const placeExitAllOrders = async () => {
  try {
    const orderData = {
      order_type: 'exit_all_positions'
    };

    const response = await fetch(getApiUrl(API_ENDPOINTS.ORDERS), {
      method: 'POST',
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify(orderData)
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data?.message || 'Failed to exit all positions');
    }

    return data;
  } catch (error) {
    console.error('Error exiting all positions:', error);
    throw error;
  }
};

/**
 * Fetch order history from the API
 * @param {Number} limit - Maximum number of orders to fetch (default 100)
 * @returns {Promise<Object>} - API response with orders array
 */
export const fetchOrderHistory = async (limit = 100) => {
  try {
    const response = await fetch(
      getApiUrl(`${API_ENDPOINTS.ORDER_HISTORY}?limit=${limit}`),
      {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      }
    );

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || 'Failed to fetch order history');
    }

    return data;
  } catch (error) {
    console.error('Error fetching order history:', error);
    throw error;
  }
};

/**
 * Fetch ALL failed orders across all indices and expiries
 * @returns {Promise<Array>} - Array of failed orders
 */
export const getFailedOrders = async () => {
  try {
    const response = await fetch(
      getApiUrl(API_ENDPOINTS.FAILED_ORDERS),
      {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      }
    );

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || 'Failed to fetch failed orders');
    }

    return data || [];
  } catch (error) {
    console.error('Error fetching failed orders:', error);
    throw error;
  }
};

/**
 * Remove a failed order by ID
 * @param {String} orderID - Failed order ID
 * @param {String} index - Index name
 * @param {String} expiry - Expiry date
 * @returns {Promise<Object>} - API response
 */
export const removeFailedOrder = async (orderID, index, expiry) => {
  try {
    const response = await fetch(
      getApiUrl(`${API_ENDPOINTS.FAILED_ORDERS}/${orderID}?index=${index}&expiry=${expiry}`),
      {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      }
    );

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || 'Failed to remove failed order');
    }

    return data;
  } catch (error) {
    console.error('Error removing failed order:', error);
    throw error;
  }
};

/**
 * Get maximum affordable quantity for a symbol at given price
 * @param {String} symbol - Trading symbol
 * @param {Number} price - Current price
 * @returns {Promise<Object>} - Object with max_quantity
 */
export const getMaxAffordableQuantity = async (symbol, price) => {
  try {
    const response = await fetch(
      getApiUrl(`${API_ENDPOINTS.FAILED_ORDERS}/max-quantity?symbol=${symbol}&price=${price}`),
      {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      }
    );

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || 'Failed to get max affordable quantity');
    }

    return data;
  } catch (error) {
    console.error('Error getting max affordable quantity:', error);
    throw error;
  }
};

/**
 * Retry a failed order with updated quantity
 * @param {String} failedOrderID - Failed order ID
 * @param {Number} updatedQty - Updated quantity to retry with
 * @param {String} index - Index name
 * @param {String} expiry - Expiry date
 * @returns {Promise<Object>} - API response with new order details
 */
export const retryOrder = async (failedOrderID, updatedQty, index, expiry) => {
  try {
    const response = await fetch(
      getApiUrl(API_ENDPOINTS.RETRY_ORDER),
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          failed_order_id: failedOrderID,
          updated_quantity: updatedQty,
          index: index,
          expiry: expiry,
        }),
      }
    );

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || 'Failed to retry order');
    }

    // Validate order placement status
    if (data.orders && data.orders.length > 0) {
      const hasSuccess = data.orders.some(order =>
        ['PLACED', 'COMPLETE', 'EXECUTED', 'success'].includes(order.status)
      );
      if (!hasSuccess) {
        const statuses = data.orders.map(o => o.status).join(', ');
        throw new Error(`Order retry failed with status: ${statuses}`);
      }
    } else if (!data.success) {
      throw new Error(data.message || 'Order retry failed');
    }

    return data;
  } catch (error) {
    console.error('Error retrying order:', error);
    throw error;
  }
};
